import type {
  CSSProperties,
  ElementType,
  HTMLAttributes,
  ReactNode,
} from 'react'
import { Badge } from '@/components/ui/badge'
import { formatEnumLabel } from '@/lib/labels/enum-label'
import { getStatusPillStyle } from '@/lib/theme/color-maps'
import { cn } from '@/lib/utils'

/**
 * The one status pill (design audit S11). Five tones, two variants, 12 px,
 * 24 px tall, Title Case label, and always a leading dot or icon so a status
 * never depends on colour alone. Custom statuses pass their own hex through
 * `color` and render the soft variant in that hue via the same light/dark
 * `--status-pill-*` variables the table pills already use.
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'
export type StatusBadgeVariant = 'soft' | 'outline'

export type StatusPillCssVariables = CSSProperties & {
  '--status-pill-bg-light'?: string
  '--status-pill-text-light'?: string
  '--status-pill-border-light'?: string
  '--status-pill-bg-dark'?: string
  '--status-pill-text-dark'?: string
  '--status-pill-border-dark'?: string
}

export interface StatusBadgeProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'color'
> {
  /** Raw enum or human label; machine constants are Title Cased. */
  label: string
  tone?: StatusTone
  variant?: StatusBadgeVariant
  /** A record's own hex colour. Wins over `tone` when it parses. */
  color?: string | null
  /** Phosphor icon. Without one a 6 px dot leads the label. */
  icon?: ElementType
  /** Trailing node, e.g. a stage probability. */
  suffix?: ReactNode
  /** Hide the text and keep it for screen readers only. */
  labelHidden?: boolean
}

// Tailwind hue scales rather than the semantic tokens: `--warning` is a
// 67 % lightness yellow and cannot carry text, and the tokens are not exposed
// as Tailwind colour classes. No hex here, so the colour-literal lint passes.
const TONE_TEXT_BORDER: Record<StatusTone, string> = {
  neutral:
    'border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-300',
  info: 'border-sky-200 text-sky-700 dark:border-sky-800 dark:text-sky-300',
  success:
    'border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300',
  warning:
    'border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300',
  danger: 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-300',
}

const TONE_SOFT_BG: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 dark:bg-slate-800/60',
  info: 'bg-sky-50 dark:bg-sky-900/30',
  success: 'bg-emerald-50 dark:bg-emerald-900/30',
  warning: 'bg-amber-50 dark:bg-amber-900/30',
  danger: 'bg-red-50 dark:bg-red-900/30',
}

/** CSS colour strings for inline styles (chart dots, filter chips, rails). */
const TONE_CSS_COLOR: Record<StatusTone, string> = {
  neutral: 'hsl(var(--muted-foreground))',
  info: 'hsl(var(--info))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  danger: 'hsl(var(--destructive))',
}

export function toneColor(tone: StatusTone): string {
  return TONE_CSS_COLOR[tone]
}

/**
 * Soft tint plus readable text for an identity chip that shares a badge's
 * tone (a sprint or cycle section's icon next to its status pill).
 */
export function toneChipClasses(tone: StatusTone): string {
  return cn(TONE_SOFT_BG[tone], TONE_TEXT_BORDER[tone])
}

export const STATUS_BADGE_BASE_CLASSES =
  'inline-flex h-6 max-w-full items-center gap-1.5 rounded-full border px-2 py-0 text-xs font-medium'

/** Colour classes that read the `statusPillVariables` CSS variables. */
export const STATUS_PILL_VAR_CLASSES = [
  '[background-color:var(--status-pill-bg-light)]',
  '[color:var(--status-pill-text-light)]',
  '[border-color:var(--status-pill-border-light)]',
  'dark:[background-color:var(--status-pill-bg-dark)]',
  'dark:[color:var(--status-pill-text-dark)]',
  'dark:[border-color:var(--status-pill-border-dark)]',
]

/**
 * Tone + variant classes without the badge element, for controls that must
 * stay a <button> (the role dropdown in Admin members).
 */
export function statusBadgeClasses({
  tone = 'neutral',
  variant = 'soft',
}: {
  tone?: StatusTone
  variant?: StatusBadgeVariant
}): string {
  return cn(
    STATUS_BADGE_BASE_CLASSES,
    TONE_TEXT_BORDER[tone],
    variant === 'soft' ? TONE_SOFT_BG[tone] : 'bg-transparent',
  )
}

/** Inline CSS variables for a record's own hue, or undefined when not hex. */
export function statusPillVariables(
  color?: string | null,
): StatusPillCssVariables | undefined {
  if (!color) return undefined
  const style = getStatusPillStyle(color)
  if (!style) return undefined
  return {
    '--status-pill-bg-light': style.backgroundColor,
    '--status-pill-text-light': style.color,
    '--status-pill-border-light': style.borderColor,
    '--status-pill-bg-dark': style['--status-pill-bg-dark'],
    '--status-pill-text-dark': style['--status-pill-text-dark'],
    '--status-pill-border-dark': style['--status-pill-border-dark'],
  }
}

export function StatusBadge({
  label,
  tone = 'neutral',
  variant = 'soft',
  color,
  icon: Icon,
  suffix,
  labelHidden = false,
  className,
  style,
  ...rest
}: StatusBadgeProps) {
  const pillVariables = statusPillVariables(color)
  const hasOwnHue = pillVariables !== undefined
  const text = formatEnumLabel(label)

  return (
    <Badge
      variant="outline"
      className={cn(
        STATUS_BADGE_BASE_CLASSES,
        hasOwnHue
          ? STATUS_PILL_VAR_CLASSES
          : [
              TONE_TEXT_BORDER[tone],
              variant === 'soft' ? TONE_SOFT_BG[tone] : 'bg-transparent',
            ],
        className,
      )}
      style={pillVariables ? { ...pillVariables, ...style } : style}
      {...rest}
    >
      {Icon ? (
        <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      ) : (
        <span
          aria-hidden
          className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current"
          style={hasOwnHue && color ? { backgroundColor: color } : undefined}
        />
      )}
      <span className={labelHidden ? 'sr-only' : 'truncate'}>{text}</span>
      {suffix}
    </Badge>
  )
}
